import SEO from "../components/SEO";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle, Calendar, Clock, Users, Star, TrendingUp, Home, Heart, Menu, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

// ============================================
// ZOOM LINK - UPDATE THIS WHEN CHANGING WEBINAR
// This is the ONLY place the Zoom link is defined for this page
// All "Register" buttons that go directly to Zoom use this
// ============================================
const ZOOM_WEBINAR_LINK = "https://zoom.us/webinar/register/WN__fC109gdSYC3mFsF8cBJJA";
// ============================================

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/yyki0ikq_88a27972352a4e7fd879af6447b80c0ea7884c320d451a34d069e1bedb549bb4.png";
const HAPPY_EMPLOYEES = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/8bvbouj3_9aa9ffbb736b703184bd002f745d4f337501b5bca1882349e2124d9dbe1a7b72.png";
const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/yyki0ikq_88a27972352a4e7fd879af6447b80c0ea7884c320d451a34d069e1bedb549bb4.png";

const EventsPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  const discoveries = [
    { icon: TrendingUp, text: "Why homeowners stay 3x longer than renters" },
    { icon: Users, text: "The hidden retention cost inside your workforce" },
    { icon: Heart, text: "The connection between housing and mental wellness" },
    { icon: Home, text: "How to structure employer down payment contributions" },
    { icon: Star, text: "How to implement without adding HR burden" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white"
      data-testid="events-page"
    >
      <SEO title="The Big Shift Webinar" description="Join CityWorth's webinar to learn how homeownership is the future of employee retention. Homeowners stay 3x longer than renters." />
      {/* Navigation - Matching Homepage Style */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" />
          </Link>
          
          <div className="flex items-center gap-4">
            {/* Get Started Button */}
            <Link to="/get-started">
              <Button className="btn-primary text-white px-6 py-2.5">Get Started</Button>
            </Link>
            
            {/* Dropdown Menu */}
            <div className="relative">
              <Button 
                variant="outline" 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 border-slate-300 px-4 py-2.5"
              >
                <Menu className="w-5 h-5" />
                <span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              <AnimatePresence>
                {isMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                    >
                      {menuItems.map((item, i) => (
                        <button
                          key={i}
                          onClick={item.action}
                          className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                        >
                          {item.label}
                        </button>
                      ))}
                      <div className="border-t border-slate-100 my-2" />
                      {/* ZOOM LINK BUTTON */}
                      <a 
                        href={ZOOM_WEBINAR_LINK} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block px-4 py-3 text-red-600 hover:bg-red-50 font-medium transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Register for Webinar →
                      </a>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Matching Homepage Style */}
      <section id="top" className="pt-12 pb-20 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-red-50/30" />
        <div className="absolute top-20 right-0 w-1/2 h-96 bg-gradient-to-l from-blue-50/50 to-transparent" />
        
        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-6">
                <Calendar className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-700">Live Online Event</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                The Big{" "}
                <span className="gradient-text">Shift</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-600 mb-4">
                Why Homeownership Is Becoming the Next Major Employee Benefit
              </p>
              
              <p className="text-lg text-slate-500 mb-8 max-w-lg">
                Join forward-thinking HR leaders and executives discovering the retention strategy that's transforming workplaces.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                {/* ZOOM LINK BUTTON - Goes directly to Zoom registration */}
                <a href={ZOOM_WEBINAR_LINK} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg">
                    Reserve Your Free Spot
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/get-started">
                  <Button size="lg" variant="outline" className="px-8 py-6 text-lg border-slate-300">
                    Contact Us
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-8 text-slate-500">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>30 Minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>HR Leaders & Executives</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Live with Q&A</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Happy employees and homeowners" 
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-lg border border-slate-100"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">3x</div>
                    <div className="text-sm text-slate-500">Longer Retention</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Problem Hook */}
      <section className="py-20 bg-red-600 text-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Most companies are solving the wrong problem.
            </h2>
            <p className="text-xl md:text-2xl text-white/90">
              They think recruiting is the challenge. <span className="font-bold underline">The real problem is stability.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Story Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Perks aren't working{" "}
                <span className="gradient-text">anymore.</span>
              </h2>
              
              <div className="flex flex-wrap gap-3 mb-8">
                {["Gym memberships", "Pizza days", "Remote flexibility", "Unlimited PTO"].map((perk, i) => (
                  <span key={i} className="px-4 py-2 bg-white rounded-full text-slate-600 text-sm border border-slate-200">
                    {perk}
                  </span>
                ))}
              </div>

              <p className="text-lg text-slate-600 mb-6">
                Companies keep adding perks, but turnover continues. Meanwhile:
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-slate-700">
                  <span className="text-red-500 mt-1 text-xl">→</span>
                  <span className="text-lg">Housing costs are rising faster than wages</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <span className="text-red-500 mt-1 text-xl">→</span>
                  <span className="text-lg">Renters feel trapped and stressed</span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <span className="text-red-500 mt-1 text-xl">→</span>
                  <span className="text-lg">Financial stress destroys engagement</span>
                </li>
              </ul>

              <p className="text-2xl font-bold text-slate-900">
                What if the solution wasn't another perk... but <span className="gradient-text">Homeownership</span>?
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden shadow-xl"
            >
              <img 
                src={HAPPY_EMPLOYEES} 
                alt="Happy employees collaborating" 
                className="w-full h-auto object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You'll Learn Section */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              What You'll{" "}
              <span className="gradient-text">Discover</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              In just 30 minutes, learn the framework top companies are using to transform retention.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {discoveries.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-blue-500 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <p className="text-lg text-slate-700 font-medium">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-6">
              Ready to Transform Your{" "}
              <span className="gradient-text">Retention Strategy?</span>
            </h2>
            
            <p className="text-xl text-slate-600 mb-10">
              Join The Big Shift and discover why homeownership is the future of employee benefits.
            </p>

            {/* ZOOM LINK BUTTON - Goes directly to Zoom registration */}
            <a href={ZOOM_WEBINAR_LINK} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="btn-primary text-white px-12 py-6 text-xl font-semibold">
                Reserve My Free Spot
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </a>

            <div className="flex flex-wrap items-center justify-center gap-8 mt-10 text-slate-500">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>Live with Q&A</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>30 Minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span>100% Free</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/">
            <img src={LOGO_URL} alt="CityWorth" className="h-8" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/careers" className="text-slate-600 hover:text-slate-900">Careers</Link>
            <Link to="/get-started" className="text-slate-600 hover:text-slate-900">Contact</Link>
          </div>
          <p className="text-slate-500 text-sm">© {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </motion.div>
  );
};

export default EventsPage;
